import { pipe } from 'fp-ts/lib/function';
import * as React from 'react';
import { Redirect, RouteComponentProps } from 'react-router';
import * as O from 'fp-ts/Option';

import { Link } from 'react-router-dom';
import { Container } from '../../components/Container';
import { Heading } from '../../components/Heading';
import { LoadingSpinner } from '../../components/LoadingContainer';
import {
  ReferralCodeQuery,
  useReferralCodeQuery,
} from '../../generated/graphql';

import { imageSize } from '../../utils/imagekit';
import { getInitials } from '../../utils/getInitials';
import { useStore } from '../../contexts/StoreContext';

import { paths } from '../../paths';
import { buttonClass } from '../../components/Button';
import referGift from './refer-gift.png';
import { REFERRAL_CODE_KEY } from './referralCodeKey';

type Props = RouteComponentProps<{
  code: string;
}>;

export const ReferralLanding: React.FC<Props> = ({
  match: {
    params: { code },
  },
}) => {
  const [referralCodeResponse] = useReferralCodeQuery({ variables: { code } });
  return (
    <div className="text-white h-full bg-primary-500 py-10">
      <Container>
        {referralCodeResponse.fetching ? (
          <LoadingSpinner className="mx-auto" />
        ) : (
          pipe(
            referralCodeResponse.data?.referralCode,
            O.fromNullable,
            O.fold(
              () => <Redirect to={paths.register()} />,
              (referralCode) => (
                <ReferralCodeContent referralCode={referralCode} />
              ),
            ),
          )
        )}
      </Container>
    </div>
  );
};

const ReferralCodeContent: React.FC<{
  referralCode: NonNullable<ReferralCodeQuery['referralCode']>;
}> = ({ referralCode }) => {
  const user = referralCode.user;

  const store = useStore();

  React.useEffect(() => {
    store.set(REFERRAL_CODE_KEY, referralCode.code);
  }, [referralCode.code]);

  return (
    <div className="text-center space-y-8 max-w-lg mx-auto py-10">
      <Heading className="text-4xl">You're Invited to Matchhaus!</Heading>
      <div className="px-10 py-8">
        <img
          src={referGift}
          alt="Referral Reward"
          className="mx-auto w-64 sm:w-72"
        />
      </div>

      <div className="flex items-center">
        <div className="w-12 h-12 mr-4 flex-shrink-0">
          <div className="w-full h-full rounded-full grid place-content-center bg-grey-800 text-white overflow-hidden ">
            {user?.profileImage ? (
              <img
                src={imageSize(user.profileImage.url, 'thumb')}
                alt={user.profileImage.caption || user.name}
                className="object-cover"
              />
            ) : (
              getInitials(user.name)
            )}
          </div>
        </div>
        <p className="text-3xl font-semibold text-left">
          {user.name} sent you £4 off Premium
        </p>
      </div>

      <p className="text-left">
        Create or Join your sports team in 30 seconds. Then enjoy the free plan
        or upgrade to Premium with a £4 discount.
      </p>

      <Link
        to={paths.register()}
        className={`${buttonClass} block w-full bg-green-700 border-green-700 hover:bg-green-500 hover:border-green-500 text-white`}
      >
        Sign up to claim your reward
      </Link>

      <p>We'll send you an email once your reward has been added.</p>
    </div>
  );
};
